<template>
  <v-container class="risk-category-selector-container">
    <v-row dense row wrap class="risk-category-selector-row">
      <v-col
        @click="toggleExpand"
        class="col-10 pt-4"
        :class="{
          clickable: expandable,
        }"
      >
        <span class="ml-4 name-label application-heading"
          >{{ riskCategory.display
          }}<span v-if="riskCategoriesFilterCounts" class="hyp-total"
            >{{ formatFilterCount(riskCategoriesFilterCounts, riskCategory) }}
          </span>
        </span>
      </v-col>
      <v-col class="col-2">
        <v-switch
          v-model="switchEnabled"
          @click="toggleSwitch({ mapAll: true })"
          class="event-input-toggle pa-0 ma-0 mt-1"
          solo
          item-text="display"
        ></v-switch>
      </v-col>
    </v-row>
    <v-row dense row wrap class="mt-3">
      <v-col class="col-12">
        <ul v-if="this.expanded" class="category-list">
          <li :key="index" v-for="(item, index) in availableItems">
            <v-checkbox
              v-model="item.enabled"
              @click="handleSubCategoryChange(item)"
              class="category-input"
              dense
            >
              <template v-slot:label>
                <span class="application-heading">{{ item.display }}</span>
                <span class="hyp-total">{{
                  formatFilterCount(riskCategoriesFilterCounts, item)
                }}</span>
              </template>
            </v-checkbox>
          </li>
        </ul>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import lodashGet from "lodash/get";
import { formatBigNumber } from "@/common/basic.utilities";
import {
  getRiskCategories,
  isRiskCategorySelected,
  isSubCategorySelected,
  modifyRiskCategoryString,
} from "@/services/controlRisks/risks.service";

const EMITTED_ACTIONS = {
  CHANGED: "changed",
  EXPANDED: "expanded",
};

export default {
  name: "RiskCategoryPicker",
  props: {
    riskCategory: {
      riskCategory: Object,
      required: true,
    },
    selectedRiskCategories: {
      type: String,
    },
    reset: {
      type: String,
    },
    riskCategoriesFilterCounts: {
      type: Object,
    },
    expanded: {
      type: Boolean,
      default: false,
    },
    mapAll: {
      type: Object,
    },
  },
  data() {
    return {
      availableItems: [],
      previousSelectedRiskCategories: null,
      switchEnabled: false,
    };
  },
  computed: {
    riskCategories() {
      let categories = [];
      if (this.riskCategory) {
        categories = getRiskCategories({ ui: true });
      }
      return categories;
    },
    expandable() {
      return lodashGet(this, "riskCategory.subtopics.length") > 0;
    },
    hasSubtopics() {
      return (
        this.riskCategory &&
        this.riskCategory.subtopics &&
        this.riskCategory.subtopics.length > 0
      );
    },
  },
  mounted() {
    this.initialize();
  },
  methods: {
    initialize() {
      this.determineSwitchEnabled();
      this.setupSubtopics();

      let hasSelectedItem = false;
      this.availableItems.forEach((item) => {
        if (item.enabled) {
          hasSelectedItem = true;
        }
      });

      if (
        (hasSelectedItem && !this.expanded) ||
        (!hasSelectedItem && this.expanded)
      ) {
        this.toggleExpand();
      }

      this.previousSelectedRiskCategories = this.selectedRiskCategories;
    },
    formatFilterCount(riskCategoryFilterCounts, riskCategory) {
      let count = lodashGet(riskCategoryFilterCounts, riskCategory.name);
      if (count || count === 0) {
        return `(${formatBigNumber(count)})`;
      }
    },
    toggleExpand() {
      if (this.expandable) {
        //don't close an open section you're turning on
        if (this.switchEnabled && this.expanded) {
          return;
        }

        //don't open a collapsed section you're turning off
        if (!this.switchEnabled && !this.expanded) {
          return;
        }

        let expanded = !this.expanded;
        this.$emit(EMITTED_ACTIONS.EXPANDED, {
          expanded: expanded,
          riskCategory: this.riskCategory.id,
        });
      }
    },
    determineSwitchEnabled() {
      let res = false;
      if (
        isRiskCategorySelected(
          this.riskCategory.id,
          this.selectedRiskCategories
        )
      ) {
        res = true;
      }

      if (this.riskCategory.subtopics) {
        this.riskCategory.subtopics.forEach((subtopic) => {
          if (isSubCategorySelected(subtopic.id, this.selectedRiskCategories)) {
            res = true;
          }
        });
      }
      this.switchEnabled = res;
    },
    toggleSwitch(opts = {}) {
      let resultString = modifyRiskCategoryString(
        this.riskCategory["id"],
        this.switchEnabled,
        this.selectedRiskCategories
      );
      this.$emit(EMITTED_ACTIONS.CHANGED, {
        selectedRiskCategories: resultString,
      });
    },
    setupSubtopics() {
      if (
        this.riskCategory.subtopics &&
        Array.isArray(this.riskCategory.subtopics)
      ) {
        this.availableItems = this.riskCategory.subtopics;
        for (let i = 0; i < this.availableItems.length; i++) {
          this.availableItems[i].enabled = isSubCategorySelected(
            this.availableItems[i].id,
            this.selectedRiskCategories,
            this.riskCategories
          );
        }
      } else {
        this.availableItems = [];
      }
    },
    handleSubCategoryChange(item) {
      let resultString = modifyRiskCategoryString(
        item["id"],
        item.enabled,
        this.selectedRiskCategories
      );
      this.$emit(EMITTED_ACTIONS.CHANGED, {
        selectedRiskCategories: resultString,
      });
    },
  },
  watch: {
    mapAll: {
      handler() {
        this.switchEnabled = this.mapAll.enabled;
        this.toggleSwitch({ mapAll: this.mapAll.enabled });
      },
      deep: true,
    },
    riskCategory: {
      handler() {
        this.initialize();
      },
      deep: true,
    },
    selectedRiskCategories: {
      handler() {
        this.initialize();
      },
    },
  },
};
</script>

<style scoped>
.risk-category-selector-container {
  padding: 0 0 5px 0 !important;
}
.risk-category-selector-row {
  height: 20px;
}
.name-label {
  color: #1c1c1c;
  font-size: 14px !important;
  margin: 0 0 0 5px !important;
  position: relative;
  top: -8px;
}
.category-list {
  list-style-type: none;
  padding: 0 0 0 16px;
}
.category-input {
  margin: 4px;
}
.category-input >>> .v-label {
  color: #1c1c1c;
  font-size: 13px !important;
}
.category-input >>> .v-messages {
  min-height: 0 !important;
}
.category-input >>> .v-input__slot {
  margin: 0 !important;
}
.hyp-total {
  font-size: 12px;
  position: relative;
  top: -1px;
  padding-left: 5px;
}
.clickable {
  cursor: pointer;
}
</style>
